import {
  ABACPolicy,
  useCreateABACPolicyMutation,
  useDeleteABACPolicyMutation,
  useUpdateABACPolicyMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseABACPolicyMutationProps = {}

export type UseABACPolicyMutationResult = {
  createABACPolicy: (abacPolicy: Partial<ABACPolicy>, onSuccess: ((abacPolicy: ABACPolicy) => void) | undefined) => void
  updateABACPolicy: (abacPolicy: Partial<ABACPolicy>, onSuccess: (() => void) | undefined) => void
  deleteABACPolicy: (abacPolicyId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useABACPolicyMutation = (props: UseABACPolicyMutationProps = {}): UseABACPolicyMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createABACPolicy, {isLoading: isCreating}] = useCreateABACPolicyMutation()
  const [_updateABACPolicy, {isLoading: isUpdating}] = useUpdateABACPolicyMutation()
  const [_deleteABACPolicy, {isLoading: isDeleting}] = useDeleteABACPolicyMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useABACPolicyUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.abacpolicy", "ABAC Policy"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useABACPolicyUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useABACPolicyUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.abacpolicy", "ABAC Policy"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useABACPolicyUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useABACPolicyUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.abacpolicy", "ABAC Policy"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useABACPolicyUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ABACPolicy>, onSuccess: ((abacPolicy: ABACPolicy) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.abacpolicy", "ABAC Policy") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.abacpolicy", "ABAC Policy") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.abacpolicy", "ABAC Policy") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((abacPolicyId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteABACPolicy({id: abacPolicyId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteABACPolicy, handleDeleteResult])

  const createABACPolicy = useCallback((abacPolicy: Partial<ABACPolicy>, onSuccess: ((abacPolicy: ABACPolicy) => void) | undefined) => {
    const promise = _createABACPolicy(abacPolicy).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createABACPolicy, handleCreateResult])

  const updateABACPolicy = useCallback((abacPolicy: Partial<ABACPolicy>, onSuccess: (() => void) | undefined) => {
    const promise = _updateABACPolicy(abacPolicy).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateABACPolicy, handleUpdateResult])

  const deleteABACPolicy = useCallback((abacPolicyId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.abacpolicy", "ABAC Policy"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.abacpolicy.confirm", "Are you sure you want to delete this ABAC Policy?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(abacPolicyId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createABACPolicy: createABACPolicy,
    updateABACPolicy: updateABACPolicy,
    deleteABACPolicy: deleteABACPolicy,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
